import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import dayjs from 'dayjs'
import { dashboardApi, projectsApi, timeEntriesApi } from '@/api/endpoints'
import type { ClientCommitment, EarnedAmount, LastBilling, Project, TimeEntry, TodaySummary } from '@/api/types'
import { useTimerStore } from '@/stores/useTimerStore'
import EditEntryModal from '@/components/EditEntryModal'
import { Button, Card, Select } from '@/components/ui'
import {
  formatDate,
  formatDecimalHours,
  formatDuration,
  formatHours,
  formatMonth,
  formatMoney,
  formatTime,
} from '@/utils/format'

function useNow(active: boolean) {
  const [, tick] = useState(0)
  useEffect(() => {
    if (!active) return
    const id = setInterval(() => tick((n) => n + 1), 1000)
    return () => clearInterval(id)
  }, [active])
}

export default function TodayPage() {
  const { running, refresh, start, stop } = useTimerStore()
  const [projects, setProjects] = useState<Project[]>([])
  const [projectId, setProjectId] = useState('')
  const [description, setDescription] = useState('')
  const [entries, setEntries] = useState<TimeEntry[]>([])
  const [summary, setSummary] = useState<TodaySummary | null>(null)
  const [editing, setEditing] = useState<TimeEntry | null>(null)
  const today = dayjs().format('YYYY-MM-DD')

  useNow(!!running)

  const loadEntries = async () => setEntries(await timeEntriesApi.list(today, today))
  const loadSummary = async () => setSummary(await dashboardApi.today())
  const reload = () => Promise.all([loadEntries(), loadSummary()])

  useEffect(() => {
    projectsApi.list().then(setProjects)
    refresh()
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Souhrn počítá běžící stopky do teď, ale je to snímek — dokud se měří,
  // necháme ho jednou za minutu dojít. Vteřinový tik má jen běžící časomíra.
  useEffect(() => {
    if (!running) return
    const id = setInterval(loadSummary, 60_000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running])

  const elapsed = running ? dayjs().diff(dayjs(running.startTime), 'second') : 0
  // Souhrn počítá i běžící stopky; než dojde, sečteme aspoň dokončené záznamy,
  // ať v kartě nebliká jiné číslo než v dlaždici „Dnes".
  const completedToday = useMemo(() => entries.reduce((s, e) => s + (e.durationSecs ?? 0), 0), [entries])
  const totalToday = summary?.todaySecs ?? completedToday

  const onStart = async () => {
    if (!projectId) return
    await start(projectId, description || undefined)
    setDescription('')
    await reload()
  }
  const onStop = async () => {
    await stop()
    await reload()
  }
  // Pokračování = nové stopky se stejným projektem a popisem. Nabízí se jen
  // když nic neběží — jinak by kliknutí tiše zastavilo rozdělanou práci.
  const onResume = async (entry: TimeEntry) => {
    await start(entry.projectId, entry.description ?? undefined)
    await reload()
  }
  const onDelete = async (id: string) => {
    await timeEntriesApi.remove(id)
    await reload()
  }
  const onSaved = async () => {
    setEditing(null)
    await Promise.all([reload(), refresh()]) // refresh: úprava mohla trefit běžící záznam
  }

  return (
    <div className="space-y-6">
      <Card>
        {running ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="text-sm text-slate-500">
                {running.clientName} · {running.projectName}
              </div>
              <div className="text-lg font-medium">{running.description || 'Bez popisu'}</div>
            </div>
            <div className="flex items-center justify-between gap-4 sm:justify-end">
              <span className="font-mono text-2xl tabular-nums">{formatDuration(elapsed)}</span>
              <Button variant="danger" onClick={onStop}>
                Zastavit
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-48 flex-1">
              <Select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                <option value="">Vyber projekt…</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.clientName} · {p.name}
                  </option>
                ))}
              </Select>
            </div>
            <input
              className="min-w-48 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500"
              placeholder="Co děláš?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onStart()}
            />
            <Button onClick={onStart} disabled={!projectId}>
              Spustit
            </Button>
          </div>
        )}
      </Card>

      {summary && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Stat label="Dnes" value={formatHours(summary.todaySecs)} earnings={summary.todayEarnings} />
          <Stat
            label="Tento týden"
            value={formatHours(summary.weekSecs)}
            earnings={summary.weekEarnings}
            hint={`${formatDate(summary.weekFrom)} – ${formatDate(summary.weekTo)}`}
          />
          <Stat
            label="Tento měsíc"
            value={formatHours(summary.monthSecs)}
            earnings={summary.monthEarnings}
            hint={formatMonth(summary.monthFrom)}
          />
        </div>
      )}

      {summary && summary.commitments.length > 0 && (
        <Card>
          <h2 className="mb-4 font-semibold">Úvazek</h2>
          <div className="space-y-5">
            {summary.commitments.map((c) => (
              <CommitmentRow key={c.clientId} commitment={c} />
            ))}
          </div>
        </Card>
      )}

      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-semibold">Dnešní záznamy</h2>
          <span className="text-sm text-slate-500">Celkem {formatHours(totalToday)}</span>
        </div>
        {entries.length === 0 ? (
          <p className="text-sm text-slate-400">Zatím žádné záznamy.</p>
        ) : (
          // Místo tabulky seznam: na širokém displeji vyjde stejně do sloupců,
          // na telefonu se popis i akce zalomí pod sebe místo mačkání do pěti
          // buněk, které se do jeho šířky nevejdou.
          <ul className="text-sm">
            {entries.map((e) => (
              <li key={e.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-slate-100 py-2">
                <span className="tabular-nums text-slate-500 sm:w-28">
                  {formatTime(e.startTime)}
                  {e.endTime ? `–${formatTime(e.endTime)}` : '…'}
                </span>
                <span className="w-full min-w-0 sm:w-auto sm:flex-1">
                  <span className="text-slate-500">{e.clientName} ·</span> {e.projectName}
                  {e.description && <span className="text-slate-400"> — {e.description}</span>}
                </span>
                <span className="ml-auto font-mono tabular-nums sm:ml-0">
                  {e.durationSecs != null ? formatDuration(e.durationSecs) : 'běží'}
                </span>
                <span className="flex shrink-0 gap-3 sm:pl-3">
                  {!running && e.endTime && (
                    <button
                      className="text-xs text-brand-600 hover:underline"
                      title="Znovu spustit stopky na stejném projektu"
                      onClick={() => onResume(e)}
                    >
                      pokračovat
                    </button>
                  )}
                  <button className="text-xs text-brand-600 hover:underline" onClick={() => setEditing(e)}>
                    upravit
                  </button>
                  <button className="text-xs text-red-500 hover:underline" onClick={() => onDelete(e.id)}>
                    smazat
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {summary?.lastBilling && <LastBillingCard billing={summary.lastBilling} />}

      {editing && <EditEntryModal entry={editing} onClose={() => setEditing(null)} onSaved={onSaved} />}
    </div>
  )
}

/**
 * Odpracovaný čas a k němu peníze za stejné období. Měny se nesčítají, každá
 * má vlastní řádek — kdo fakturuje v korunách i eurech, uvidí obojí zvlášť.
 */
function Stat({
  label,
  value,
  earnings,
  hint,
}: {
  label: string
  value: ReactNode
  earnings?: EarnedAmount[]
  hint?: string
}) {
  return (
    <Card>
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
      {earnings &&
        (earnings.length === 0 ? (
          <div className="mt-1 text-sm text-slate-400">bez fakturovatelných hodin</div>
        ) : (
          earnings.map((e) => (
            <div key={e.currency} className="mt-1 text-sm font-medium tabular-nums text-brand-700">
              {formatMoney(e.amount, e.currency)}
            </div>
          ))
        ))}
      {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
    </Card>
  )
}

/**
 * Plnění úvazku jednoho klienta. Cíl na týden je poměrná část měsíčního
 * úvazku (denní norma × pracovní dny týdne) — počítá ho backend, tady se
 * jen ukazuje, kolik do něj a do celého měsíce chybí.
 */
function CommitmentRow({ commitment: c }: { commitment: ClientCommitment }) {
  const progress = c.monthlyHours > 0 ? Math.min(100, (c.monthHours / c.monthlyHours) * 100) : 0
  const over = c.monthHours > c.monthlyHours

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium">{c.clientName}</span>
        <span className="text-sm tabular-nums text-slate-500">
          {formatDecimalHours(c.monthHours)} z {formatDecimalHours(c.monthlyHours)}
        </span>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full ${over ? 'bg-emerald-500' : 'bg-brand-500'}`}
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="mt-2 flex flex-wrap gap-x-8 gap-y-1 text-sm">
        <span>
          Do konce týdne:{' '}
          {c.weekRemainingHours > 0 ? (
            <strong className="tabular-nums">{formatDecimalHours(c.weekRemainingHours)}</strong>
          ) : (
            <strong className="text-emerald-600">splněno</strong>
          )}
          <span className="ml-1 text-slate-400">
            (cíl {formatDecimalHours(c.weekTargetHours)}, odpracováno {formatDecimalHours(c.weekHours)})
          </span>
        </span>
        <span>
          Do úvazku:{' '}
          {c.monthRemainingHours > 0 ? (
            <strong className="tabular-nums">{formatDecimalHours(c.monthRemainingHours)}</strong>
          ) : (
            <strong className="text-emerald-600">splněno</strong>
          )}
        </span>
      </div>
    </div>
  )
}

function LastBillingCard({ billing: b }: { billing: LastBilling }) {
  return (
    <Card>
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="font-semibold">Poslední vyúčtování</h2>
        <span className="text-xs text-slate-400">uzavřeno {formatDate(b.createdAt)}</span>
      </div>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="font-medium">
            {b.clientName} · {b.projectName}
          </div>
          <div className="text-sm text-slate-500">
            {formatDate(b.periodFrom)} – {formatDate(b.periodTo)} · {formatDecimalHours(b.billedHours)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xl font-semibold tabular-nums">{formatMoney(b.amount, b.currency)}</div>
          <div className="text-xs">
            {b.status === 'INVOICED' ? (
              b.invoiceUrl ? (
                <a href={b.invoiceUrl} target="_blank" rel="noreferrer" className="text-brand-600 hover:underline">
                  faktura {b.invoiceNumber ?? ''}
                </a>
              ) : (
                <span className="text-slate-500">vyfakturováno</span>
              )
            ) : (
              <span className="text-amber-600">zatím nevyfakturováno</span>
            )}
          </div>
        </div>
      </div>
    </Card>
  )
}
